import { en } from './en';

// The languages of the web app and the mobile app (TF-183, TF-269): one entry each, plus one
// dictionary file. Nothing in the screens names a language; they ask its entry here. Adding one is
// translation work: a dictionary (checked by dictionaries.test.ts), an entry below with
// `released: false` for review, and `released: true` when a native speaker has read it. The web
// app's calendar also needs the language's FullCalendar words (apps/web/src/i18n/calendar.ts).

export type Dictionary = Readonly<Partial<Record<string, string>>>;

/** The languages the back end writes in (sign-in pages, e-mails, the language stored at sign-up). */
export type BackendLanguage = 'en' | 'lv';

export interface LanguageEntry {
  /** The code it is known by: kept in this browser, and the page's <html lang>. */
  code: string;
  /** Its own name, as the language menu lists it. */
  name: string;
  /** The tag numbers, plural forms and the app's own dates are written with. */
  intl: string;
  /** The tag of the date fields, calendars and clock times (TF-92): "en-GB", not American English. */
  dates: string;
  /** The nearest language the back end writes in; where it differs, the language menu says so. */
  backend: BackendLanguage;
  /** Customers see released languages only; the others need the review setting (reviewLanguages). */
  released: boolean;
  /** Keys whose text is rightly the same as English, so the completeness check lets them be absent. */
  sameAsEnglish?: readonly string[];
  /** Its dictionary: English is in the main bundle; any other language is downloaded when chosen. */
  load: () => Promise<Dictionary>;
}

export const LANGUAGES = [
  {
    code: 'en',
    name: 'English',
    intl: 'en',
    dates: 'en-GB',
    backend: 'en',
    released: true,
    load: async () => en,
  },
  {
    code: 'lv',
    name: 'Latviešu',
    intl: 'lv',
    dates: 'lv',
    backend: 'lv',
    released: true,
    load: () => import('./lv').then((module) => module.lv),
  },
  {
    // Estonian (TF-184): drafted with the glossary beside it (et.glossary.md); released once a native
    // speaker has read it in the running app.
    code: 'et',
    name: 'Eesti',
    intl: 'et',
    dates: 'et',
    backend: 'en',
    released: false,
    load: () => import('./et').then((module) => module.et),
  },
  {
    // Lithuanian (TF-185): four plural forms (one, few, many, other) and names never inflected, drafted
    // with the glossary beside it (lt.glossary.md); released once a native speaker has read it.
    code: 'lt',
    name: 'Lietuvių',
    intl: 'lt',
    dates: 'lt',
    backend: 'en',
    released: false,
    load: () => import('./lt').then((module) => module.lt),
  },
  {
    // Swedish (TF-190): one Swedish for Sweden and Finland, informal "du", no form of address (Swedish
    // business uses the name); words sort with å, ä, ö after z (useI18n().compare). Released once a
    // native speaker has read it (sv.glossary.md beside it).
    code: 'sv',
    name: 'Svenska',
    intl: 'sv',
    dates: 'sv',
    backend: 'en',
    released: false,
    load: () => import('./sv').then((module) => module.sv),
  },
  {
    // Finnish (TF-189): informal address; an inserted name, file or date is never inflected - the
    // sentence is built around it in the nominative ("Tiedosto: {name}") - and after a number other
    // than one the noun is in the partitive (.other). Released once a native speaker has read it.
    code: 'fi',
    name: 'Suomi',
    intl: 'fi',
    dates: 'fi',
    backend: 'en',
    released: false,
    load: () => import('./fi').then((module) => module.fi),
  },
  {
    // Polish (TF-188): four plural forms (one, few, many, other); names never inflected and nobody's
    // gender guessed - messages are impersonal, a person's action is a noun ("Zaproszenie od: {person}").
    // No form of address: Pan/Pani would ask for the vocative. Released once a native speaker has read it.
    code: 'pl',
    name: 'Polski',
    intl: 'pl',
    dates: 'pl',
    backend: 'en',
    released: false,
    load: () => import('./pl').then((module) => module.pl),
  },
  {
    // A pseudo-translation (TF-183): every text accented and a third longer, to find texts that are
    // not translated and layouts that break with longer words. Never released.
    code: 'en-XA',
    name: 'Ëñğļîšĥ, ţëšţ',
    intl: 'en',
    dates: 'en-GB',
    backend: 'en',
    released: false,
    load: () => import('./pseudo').then((module) => module.pseudoTranslate(en)),
  },
] as const satisfies readonly LanguageEntry[];

export type Language = (typeof LANGUAGES)[number]['code'];

const ENGLISH: LanguageEntry = LANGUAGES[0];

/** A language's entry; an unknown code (an old setting, a tag from elsewhere) is English. */
export function languageEntry(code: string): LanguageEntry {
  return LANGUAGES.find((entry) => entry.code === code) ?? ENGLISH;
}

export const isLanguage = (code: string | null | undefined): code is Language => LANGUAGES.some((entry) => entry.code === code);

/** The language the back end should write in for this one: the nearest it knows, English by default. */
export const backendLanguage = (code: string): BackendLanguage => languageEntry(code).backend;

/** The languages a person may choose, by their own names in alphabetical order (English, Latviešu…). */
export function choosableLanguages(reviewing: boolean): LanguageEntry[] {
  return LANGUAGES.filter((entry) => entry.released || reviewing).sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/**
 * The language for a first visit: the browser's preferred languages in order, each matched by its
 * main part ("lv-LV" is Latvian) against the released languages - and, for a reviewer, those not yet
 * released (TF-184: an Estonian reviewer starts in Estonian); English when none matches.
 */
export function preferredLanguage(browser: readonly string[], reviewing = false): Language {
  for (const tag of browser) {
    const main = tag.toLowerCase().split('-')[0];
    const match = LANGUAGES.find((entry) => (entry.released || reviewing) && entry.code.toLowerCase().split('-')[0] === main);
    if (match) return match.code;
  }
  return 'en';
}
